import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import moment from "moment";
import { useSideBySideImageOverlays } from "hooks/useMapImageOverlay";
import { useIndexImage, useIndices } from "stores";
import { useSelectedFieldContext } from "context/SelectedFieldProvider";
import SmallText from "../../Typography/SmallText";
import CloudCoverage from "../../cloud-coverage/CloudCoverage";
import DayPickerCamvio from "../../dates/DayPicker";
import { useParams } from "react-router-dom";

const ContainerStyled = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: "0px 16px",
  "& .date-column": {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0.5rem",
    "&.right": {
      alignItems: "flex-end",
    },
    "& .date-picker": {
      width: "200px",
      "& .input ": {
        backgroundColor: "#FFFFFFAA",
        "&:hover": {
          backgroundColor: "#FFFFFF",
        },
      },
    },
    "& .no-img": {
      color: "white",
      textShadow: "2px 2px 2px black",
      fontStyle: "italic",
      fontSize: "10px",
    },
  },
});

const IndicesComparatorNew = ({ initialDate, indexType }) => {
  const { t } = useTranslation();
  const { fieldId } = useParams();
  const { selectedField } = useSelectedFieldContext();

  const { getIndexOfDate, checkIfDateHasIndex } = useIndices({
    fieldId,
  });

  const [date1, setDate1] = useState(initialDate);
  const [date2, setDate2] = useState(initialDate);
  const [month1, setMonth1] = useState(initialDate);
  const [month2, setMonth2] = useState(initialDate);

  const { availableDates: availableDates1, fetchState: fetchState1 } =
    useIndices({
      fieldId,
      date: month1,
    });

  const { availableDates: availableDates2, fetchState: fetchState2 } =
    useIndices({
      fieldId,
      date: month2,
    });

  const { image: png1 } = useIndexImage({
    fieldId,
    index: indexType,
    date: date1,
    hasIndex: checkIfDateHasIndex(date1),
  });
  const { image: png2 } = useIndexImage({
    fieldId,
    index: indexType,
    date: date2,
    hasIndex: checkIfDateHasIndex(date2),
  });

  const cloudCover1 = useMemo(() => {
    const index = getIndexOfDate(date1);
    return index?.cloudCover;
  }, [date1, getIndexOfDate]);

  const cloudCover2 = useMemo(() => {
    const index = getIndexOfDate(date2);
    return index?.cloudCover;
  }, [date2, getIndexOfDate]);

  const latlongCoords = useMemo(
    () => selectedField?.coordinates?.coordinates?.[0] || [],
    [selectedField]
  );
  useSideBySideImageOverlays(png1, png2, latlongCoords);

  return (
    <ContainerStyled className="bottom-panel">
      <div className="date-column">
        <DayPickerCamvio
          isLoading={fetchState1 === "loading"}
          defaultValue={moment(initialDate)}
          availableDates={availableDates1}
          formatValue="YYYY-MM-DD"
          className="date-picker"
          onChange={setDate1}
          onMonthChange={setMonth1}
        />
        {!png1 && (
          <SmallText
            className="no-img"
            text={t("SingleFieldPage.GroundConditions.NoIndiceImageFound")}
          />
        )}
        {typeof cloudCover1 !== "undefined" && (
          <CloudCoverage cloudCoverage={cloudCover1} shouldDisplayIcon />
        )}
      </div>
      <div className="date-column right">
        <DayPickerCamvio
          isLoading={fetchState2 === "loading"}
          defaultValue={moment(initialDate)}
          availableDates={availableDates2}
          formatValue="YYYY-MM-DD"
          className="date-picker"
          onChange={setDate2}
          onMonthChange={setMonth2}
        />
        {!png2 && (
          <SmallText
            className="no-img"
            text={t("SingleFieldPage.GroundConditions.NoIndiceImageFound")}
          />
        )}
        {typeof cloudCover2 !== "undefined" && (
          <CloudCoverage cloudCoverage={cloudCover2} shouldDisplayIcon />
        )}
      </div>
    </ContainerStyled>
  );
};

export default IndicesComparatorNew;
